import {t, tp, tpl} from '../../i18n/i18n.js';
import {setRowTone} from './section-rows.js';

/**
 * A small schematic of the page's rows next to the section list: one line per row, one button
 * per block at its span of the 12-column grid. A block of a shared row carries that row's colour,
 * the same as its card in the list. The active block is highlighted and a click reports the
 * block, so the section editor can open its card. The summary names the number of blocks. A
 * cell too narrow for its label cuts it off; hovering or focusing it shows the full label in a
 * tooltip right away, above the cell and aligned with the side of the panel the cell is on.
 *
 * @param {{onSelect: (entry: string) => void}} options
 * @returns {{element: HTMLDetailsElement, update: (view: {rows: Array<Array<{entry: string, label: string, span: number, tone?: number}>>, active: string | null}) => void}}
 */
export function createPageOutline({onSelect}) {
    const element = document.createElement('details');
    element.className = 'page-outline';
    element.open = true;
    const summary = document.createElement('summary');
    const title = document.createElement('span');
    title.className = 'page-outline-title';
    title.dataset.i18n = 'pageForm.outlineTitle';
    title.textContent = t('pageForm.outlineTitle');
    const count = document.createElement('span');
    count.className = 'page-outline-count';
    summary.append(title, count);
    const rowsBox = document.createElement('div');
    rowsBox.className = 'page-outline-rows';
    const tooltip = document.createElement('div');
    tooltip.className = 'page-outline-tooltip';
    tooltip.setAttribute('aria-hidden', 'true');
    tooltip.hidden = true;
    element.append(summary, rowsBox, tooltip);

    /** The cell's accessible name already holds the label, so the tooltip is visual only. */
    function showTooltip(cell, label) {
        if (cell.scrollWidth <= cell.clientWidth) return;
        tooltip.textContent = label;
        const alignRight = cell.offsetLeft + cell.offsetWidth / 2 > element.clientWidth / 2;
        tooltip.style.top = `${cell.offsetTop}px`;
        tooltip.style.left = alignRight ? '' : `${cell.offsetLeft}px`;
        tooltip.style.right = alignRight ? `${element.clientWidth - cell.offsetLeft - cell.offsetWidth}px` : '';
        tooltip.hidden = false;
    }

    function hideTooltip() {
        tooltip.hidden = true;
    }

    function update({rows, active}) {
        hideTooltip();
        count.textContent = tp('pageForm.outlineCount', rows.flat().length);
        rowsBox.replaceChildren(...rows.map((cells) => {
            const row = document.createElement('div');
            row.className = 'page-outline-row';
            for (const {entry, label, span, tone} of cells) {
                const cell = document.createElement('button');
                cell.type = 'button';
                cell.className = 'page-outline-cell';
                cell.dataset.entry = entry;
                cell.style.gridColumn = `span ${span}`;
                setRowTone(cell, tone);
                cell.textContent = label;
                cell.setAttribute('aria-label', tpl('pageForm.outlineJump', {label}));
                if (entry === active) {
                    cell.classList.add('is-active');
                    cell.setAttribute('aria-current', 'true');
                }
                cell.addEventListener('click', () => onSelect(entry));
                cell.addEventListener('mouseenter', () => showTooltip(cell, label));
                cell.addEventListener('focus', () => showTooltip(cell, label));
                cell.addEventListener('mouseleave', hideTooltip);
                cell.addEventListener('blur', hideTooltip);
                row.appendChild(cell);
            }
            return row;
        }));
    }

    return {element, update};
}
